import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, OnDestroy, effect, input, signal } from '@angular/core'
import { ShikshaKpiIconType } from '@features/home/models/shiksha-dashboard.model'

@Component({
    selector: 'app-shiksha-kpi-card',
    templateUrl: './shiksha-kpi-card.component.html',
    styleUrl: './shiksha-kpi-card.component.scss',
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShikshaKpiCardComponent implements OnDestroy {
    readonly label = input.required<string>()
    readonly value = input.required<string>()
    readonly subLabel = input<string>('')
    readonly tooltipText = input<string>('')
    readonly iconType = input<ShikshaKpiIconType>('revenue')
    readonly coveragePercent = input<number | null>(null)

    readonly animatedValue = signal<string>('')
    private animationFrameId: number | null = null

    constructor() {
        effect(
            () => {
                this.startValueAnimation(this.value())
            },
            { allowSignalWrites: true },
        )
    }

    ngOnDestroy(): void {
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId)
            this.animationFrameId = null
        }
    }

    private startValueAnimation(rawValue: string): void {
        const parsed = this.parseAnimatedFormat(rawValue)
        if (!parsed) {
            this.animatedValue.set(rawValue)
            return
        }

        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId)
            this.animationFrameId = null
        }

        const durationMs = 900
        const startTs = performance.now()
        const target = parsed.numericValue

        const tick = (timestamp: number): void => {
            const elapsed = timestamp - startTs
            const progress = Math.min(elapsed / durationMs, 1)
            const eased = 1 - Math.pow(1 - progress, 3)
            const currentValue = target * eased

            this.animatedValue.set(this.formatAnimatedValue(parsed.kind, currentValue, parsed))

            if (progress < 1) {
                this.animationFrameId = requestAnimationFrame(tick)
                return
            }

            this.animatedValue.set(rawValue)
            this.animationFrameId = null
        }

        this.animationFrameId = requestAnimationFrame(tick)
    }

    private parseAnimatedFormat(
        rawValue: string,
    ):
        | { kind: 'inr'; numericValue: number; unit: 'Cr' | 'L' | ''; decimals: number }
        | { kind: 'percent'; numericValue: number; decimals: number }
        | null {
        const normalized = rawValue.trim()

        const inrMatch = normalized.match(/^(?:INR\s+|₹\s?)([\d,]*\.?\d+)\s*(Cr|L)?$/i)
        if (inrMatch) {
            const numberToken = inrMatch[1]?.replace(/,/g, '') ?? ''
            const numericValue = Number.parseFloat(numberToken)
            if (!Number.isFinite(numericValue)) {
                return null
            }
            const decimals = numberToken.includes('.')
                ? (numberToken.split('.')[1]?.length ?? 0)
                : 0
            const unit = (inrMatch[2] as 'Cr' | 'L' | undefined) ?? ''
            return { kind: 'inr', numericValue, unit, decimals }
        }

        const percentMatch = normalized.match(/^([\d,]*\.?\d+)%$/)
        if (percentMatch) {
            const numberToken = percentMatch[1]?.replace(/,/g, '') ?? ''
            const numericValue = Number.parseFloat(numberToken)
            if (!Number.isFinite(numericValue)) {
                return null
            }
            const decimals = numberToken.includes('.')
                ? (numberToken.split('.')[1]?.length ?? 0)
                : 0
            return { kind: 'percent', numericValue, decimals }
        }

        return null
    }

    private formatAnimatedValue(
        kind: 'inr' | 'percent',
        value: number,
        parsed:
            | { kind: 'inr'; numericValue: number; unit: 'Cr' | 'L' | ''; decimals: number }
            | { kind: 'percent'; numericValue: number; decimals: number },
    ): string {
        if (kind === 'percent') {
            return `${value.toFixed(parsed.decimals)}%`
        }

        const inrParsed = parsed as {
            kind: 'inr'
            numericValue: number
            unit: 'Cr' | 'L' | ''
            decimals: number
        }
        if (inrParsed.unit) {
            return `₹${value.toFixed(inrParsed.decimals)} ${inrParsed.unit}`
        }

        const rounded =
            inrParsed.decimals > 0 ? Number(value.toFixed(inrParsed.decimals)) : Math.round(value)
        return `₹${rounded.toLocaleString('en-IN', {
            minimumFractionDigits: inrParsed.decimals,
            maximumFractionDigits: inrParsed.decimals,
        })}`
    }
}
